// src/App.js
import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Button, Layout, Modal, Space } from "antd";
import "./App.css";

import HeaderBar from "./components/Header/HeaderBar";
import FooterItem from "./components/FooterItem/FooterItem";

import Home from "./pages/Home";
import PostDetail from "./pages/PostDetail";
import Archive from "./pages/Archive";
import WritePost from "./pages/WritePost";
import About from "./pages/About";
import Me from "./pages/Me";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";

import { useAuth } from "./context/AuthContext";

const { Content } = Layout;

const CUSTOM_THEME_DEFAULT = {
  isDark: true,
  appBgStart: "#0f1116",
  appBgEnd: "#141922",
  appGlow1: "#565d74",
  appGlow2: "#292e42",
  headerStart: "#0f1116",
  headerEnd: "#161a22",
  headerBorder: "#2a313d",
  headerText: "#e6edf3",
  headerSubtleText: "#aeb6c2",
  cardBg: "#141820",
  cardBodyBg: "#10141c",
  cardBorder: "#2a3340",
  shadowColor: "#000000",
  textPrimary: "#e6edf3",
  textSecondary: "#aeb6c2",
  accentStart: "#3b82f6",
  accentEnd: "#22d3ee",
  tagBg: "#1c2a44",
  tagBorder: "#2f4a78",
  tagColor: "#93c5fd",
  inputBg: "#10141c",
  clockBg: "#12161e",
  clockBorder: "#2f4a78",
  clockText: "#e6edf3",
  footerBg: "#0c1016",
  footerText: "#aeb6c2",
  footerBorder: "#2a313d",
  scrollbarTrack: "#0b0f16",
  scrollbarThumb: "#2f4a78",
  scrollbarThumbHover: "#3b82f6",
};

const CUSTOM_THEME_VARS = [
  "--app-bg",
  "--header-bg",
  "--header-border",
  "--header-text",
  "--header-subtle-text",
  "--card-bg",
  "--card-border",
  "--card-shadow",
  "--card-blur",
  "--card-body-bg",
  "--accent-gradient",
  "--accent-shadow",
  "--tag-bg",
  "--tag-border",
  "--tag-color",
  "--clock-bg",
  "--clock-border",
  "--clock-text",
  "--clock-shadow",
  "--glow-1",
  "--glow-2",
  "--input-bg",
  "--text-primary",
  "--text-secondary",
  "--cover-fallback",
  "--footer-bg",
  "--footer-text",
  "--footer-border",
  "--scrollbar-track",
  "--scrollbar-thumb",
  "--scrollbar-thumb-hover",
];

const normalizeHex = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed.startsWith("#")) return "";
  if (trimmed.length === 4) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  if (trimmed.length === 7) return trimmed;
  return "";
};

const hexToRgba = (value, alpha) => {
  const hex = normalizeHex(value);
  if (!hex) return value;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const applyCustomTheme = (theme) => {
  const root = document.documentElement;
  const merged = { ...CUSTOM_THEME_DEFAULT, ...theme };
  const appBg = `radial-gradient(1200px 520px at 0% 0%, ${hexToRgba(
    merged.appGlow1,
    0.35
  )} 0%, ${hexToRgba(merged.appGlow1, 0)} 60%), radial-gradient(1100px 600px at 100% 100%, ${hexToRgba(
    merged.appGlow2,
    0.45
  )} 0%, ${hexToRgba(merged.appGlow2, 0)} 55%), linear-gradient(135deg, ${
    merged.appBgStart
  } 0%, ${merged.appBgEnd} 100%)`;
  const headerBg = `linear-gradient(135deg, ${merged.headerStart}, ${merged.headerEnd})`;
  const accentGradient = `linear-gradient(135deg, ${merged.accentStart}, ${merged.accentEnd})`;
  const glow1 = `linear-gradient(135deg, ${hexToRgba(
    merged.accentStart,
    0.8
  )}, ${hexToRgba(merged.accentStart, 0.2)})`;
  const glow2 = `linear-gradient(135deg, ${hexToRgba(
    merged.accentEnd,
    0.8
  )}, ${hexToRgba(merged.accentEnd, 0.2)})`;
  const shadowAlpha = merged.isDark ? 0.45 : 0.18;
  const clockShadowAlpha = merged.isDark ? 0.35 : 0.16;

  root.style.setProperty("--app-bg", appBg);
  root.style.setProperty("--header-bg", headerBg);
  root.style.setProperty("--header-border", merged.headerBorder);
  root.style.setProperty("--header-text", merged.headerText);
  root.style.setProperty("--header-subtle-text", merged.headerSubtleText);
  root.style.setProperty("--card-bg", merged.cardBg);
  root.style.setProperty("--card-border", merged.cardBorder);
  root.style.setProperty(
    "--card-shadow",
    `0 18px 50px ${hexToRgba(merged.shadowColor, shadowAlpha)}`
  );
  root.style.setProperty("--card-blur", "blur(12px)");
  root.style.setProperty("--card-body-bg", merged.cardBodyBg);
  root.style.setProperty("--accent-gradient", accentGradient);
  root.style.setProperty(
    "--accent-shadow",
    `0 10px 22px ${hexToRgba(merged.accentStart, 0.35)}`
  );
  root.style.setProperty("--tag-bg", merged.tagBg);
  root.style.setProperty("--tag-border", merged.tagBorder);
  root.style.setProperty("--tag-color", merged.tagColor);
  root.style.setProperty("--clock-bg", merged.clockBg);
  root.style.setProperty("--clock-border", merged.clockBorder);
  root.style.setProperty("--clock-text", merged.clockText);
  root.style.setProperty(
    "--clock-shadow",
    `0 8px 20px ${hexToRgba(merged.shadowColor, clockShadowAlpha)}`
  );
  root.style.setProperty("--glow-1", glow1);
  root.style.setProperty("--glow-2", glow2);
  root.style.setProperty("--input-bg", merged.inputBg);
  root.style.setProperty("--text-primary", merged.textPrimary);
  root.style.setProperty("--text-secondary", merged.textSecondary);
  root.style.setProperty(
    "--cover-fallback",
    `linear-gradient(135deg, ${merged.appBgStart} 0%, ${merged.appBgEnd} 100%)`
  );
  root.style.setProperty("--footer-bg", merged.footerBg);
  root.style.setProperty("--footer-text", merged.footerText);
  root.style.setProperty("--footer-border", merged.footerBorder);
  root.style.setProperty("--scrollbar-track", merged.scrollbarTrack);
  root.style.setProperty("--scrollbar-thumb", merged.scrollbarThumb);
  root.style.setProperty("--scrollbar-thumb-hover", merged.scrollbarThumbHover);
};

function RequireAuth({ children }) {
  const { isAuthed } = useAuth();
  const location = useLocation();
  if (!isAuthed) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }
  return children;
}

export default function App() {
  const location = useLocation();
  const isAuthPage = location.pathname === "/auth";
  const { isAuthed } = useAuth();
  const [introOpen, setIntroOpen] = useState(false);
  const [themeName, setThemeName] = useState(
    () => localStorage.getItem("app_theme") || "aura"
  );
  const [lastNonCustomTheme, setLastNonCustomTheme] = useState(() => {
    const stored = localStorage.getItem("last_non_custom_theme");
    if (stored && stored !== "custom") return stored;
    const initial = localStorage.getItem("app_theme") || "aura";
    return initial === "custom" ? "aura" : initial;
  });
  const [customTheme, setCustomTheme] = useState(() => {
    const raw = localStorage.getItem("custom_theme");
    if (!raw) return CUSTOM_THEME_DEFAULT;
    try {
      return { ...CUSTOM_THEME_DEFAULT, ...JSON.parse(raw) };
    } catch (error) {
      return CUSTOM_THEME_DEFAULT;
    }
  });
  const [layoutColumns, setLayoutColumns] = useState(() => {
    const raw = localStorage.getItem("layout_columns");
    const parsed = raw ? Number(raw) : 1;
    return Number.isFinite(parsed) && parsed >= 1 && parsed <= 6 ? parsed : 1;
  });
  const [groupByDay, setGroupByDay] = useState(() => {
    const raw = localStorage.getItem("home_group_by_day");
    return raw === "true";
  });
  const [bgImageEnabled, setBgImageEnabled] = useState(() => {
    const raw = localStorage.getItem("custom_bg_enabled");
    return raw === "true";
  });
  const [bgImageUrl, setBgImageUrl] = useState(() => {
    return localStorage.getItem("custom_bg_url") || "";
  });
  const [bgVideoEnabled, setBgVideoEnabled] = useState(() => {
    const raw = localStorage.getItem("bg_video_enabled");
    return raw === "true";
  });
  const [bgVideoUrl, setBgVideoUrl] = useState(() => {
    return localStorage.getItem("bg_video_url") || "/bg.mp4";
  });
  const [isImmersive, setIsImmersive] = useState(false);
  const [bgFocus, setBgFocus] = useState(false);
  const [bgFocusEnabled, setBgFocusEnabled] = useState(() => {
    const raw = localStorage.getItem("bg_focus_enabled");
    return raw !== "false";
  });
  const bgFocusTimerRef = useRef(null);
  const bgVideoUrlRef = useRef("");
  const bgVideoRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeName);
    localStorage.setItem("app_theme", themeName);
    if (themeName !== "custom") {
      setLastNonCustomTheme(themeName);
      localStorage.setItem("last_non_custom_theme", themeName);
    }
  }, [themeName]);

  useEffect(() => {
    localStorage.setItem("custom_theme", JSON.stringify(customTheme));
  }, [customTheme]);

  useEffect(() => {
    if (themeName === "custom") {
      applyCustomTheme(customTheme);
      return;
    }
    CUSTOM_THEME_VARS.forEach((key) => {
      document.documentElement.style.removeProperty(key);
    });
  }, [themeName, customTheme]);

  useEffect(() => {
    if (isAuthPage) return;
    if (!isAuthed) return;
    const shouldShow = localStorage.getItem("show_intro_modal") === "true";
    if (shouldShow) {
      setIntroOpen(true);
      localStorage.removeItem("show_intro_modal");
    }
  }, [isAuthPage, isAuthed]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--home-columns",
      String(layoutColumns)
    );
    localStorage.setItem("layout_columns", String(layoutColumns));
  }, [layoutColumns]);

  useEffect(() => {
    localStorage.setItem("home_group_by_day", String(groupByDay));
  }, [groupByDay]);

  useEffect(() => {
    localStorage.setItem("custom_bg_enabled", String(bgImageEnabled));
    localStorage.setItem("custom_bg_url", bgImageUrl);
    if (bgImageEnabled && bgImageUrl.trim()) {
      document.documentElement.style.setProperty(
        "--app-bg-image",
        `url("${bgImageUrl.trim()}")`
      );
    } else {
      document.documentElement.style.setProperty("--app-bg-image", "none");
    }
  }, [bgImageEnabled, bgImageUrl]);

  useEffect(() => {
    localStorage.setItem("bg_video_enabled", String(bgVideoEnabled));
    if (!bgVideoUrl) {
      localStorage.removeItem("bg_video_url");
    } else if (!bgVideoUrl.startsWith("blob:")) {
      localStorage.setItem("bg_video_url", bgVideoUrl);
    }
  }, [bgVideoEnabled, bgVideoUrl]);

  useEffect(() => {
    const prev = bgVideoUrlRef.current;
    if (prev && prev.startsWith("blob:") && prev !== bgVideoUrl) {
      URL.revokeObjectURL(prev);
    }
    bgVideoUrlRef.current = bgVideoUrl;
    return () => {
      if (bgVideoUrlRef.current && bgVideoUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(bgVideoUrlRef.current);
      }
    };
  }, [bgVideoUrl]);

  useEffect(() => {
    if (!bgVideoEnabled || !bgVideoUrl) return;
    const video = bgVideoRef.current;
    if (!video) return;
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === "function") {
      playAttempt.catch(() => {});
    }
  }, [bgVideoEnabled, bgVideoUrl]);

  const tryPlayVideo = () => {
    if (!bgVideoEnabled || !bgVideoUrl) return;
    const video = bgVideoRef.current;
    if (!video) return;
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === "function") {
      playAttempt.catch(() => {});
    }
  };

  useEffect(() => {
    document.documentElement.classList.toggle("bg-focus", bgFocus);
  }, [bgFocus]);

  useEffect(() => {
    localStorage.setItem("bg_focus_enabled", String(bgFocusEnabled));
  }, [bgFocusEnabled]);

  useEffect(() => {
    return () => {
      if (bgFocusTimerRef.current) {
        clearTimeout(bgFocusTimerRef.current);
      }
    };
  }, []);

  const handleLayoutClick = (event) => {
    if (isImmersive) return;
    tryPlayVideo();
    if (!bgImageEnabled || !bgFocusEnabled) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (
      target.closest(".themed-card") ||
      target.closest(".ant-card") ||
      target.closest(".ant-layout-header") ||
      target.closest(".ant-menu") ||
      target.closest(".ant-dropdown") ||
      target.closest(".ant-modal") ||
      target.closest(".ant-drawer") ||
      target.closest(".ant-input") ||
      target.closest("button")
    ) {
      return;
    }
    setBgFocus(true);
    if (bgFocusTimerRef.current) {
      clearTimeout(bgFocusTimerRef.current);
    }
    bgFocusTimerRef.current = setTimeout(() => {
      setBgFocus(false);
      bgFocusTimerRef.current = null;
    }, 1400);
  };

  const handleImmersiveEnter = () => {
    if (!bgVideoEnabled || !bgVideoUrl) return;
    setIsImmersive(true);
    const video = bgVideoRef.current;
    if (video) {
      video.muted = false;
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(() => {});
      }
    }
  };

  const handleImmersiveExit = () => {
    setIsImmersive(false);
  };

  const isDarkTheme =
    themeName === "dark" ||
    themeName === "cyberpunk" ||
    themeName === "sunset" ||
    (themeName === "custom" && customTheme.isDark);

  return (
    <Layout
      className={`app-layout${isImmersive ? " is-immersive" : ""}`}
      onClick={handleLayoutClick}
    >
      {bgVideoEnabled && bgVideoUrl ? (
        <div className="app-bg-video">
          <video ref={bgVideoRef} src={bgVideoUrl} autoPlay loop playsInline />
        </div>
      ) : null}
      {bgVideoEnabled && bgVideoUrl ? <div className="app-bg-mask" /> : null}
      {isImmersive ? (
        <div className="app-immersive-exit" onClick={handleImmersiveExit} />
      ) : null}
      {!isImmersive ? (
        <Button
          className="themed-primary immersive-toggle"
          onClick={handleImmersiveEnter}
          disabled={!bgVideoEnabled || !bgVideoUrl}
        >
          沉静观看
        </Button>
      ) : null}
      {!isAuthPage && (
        <HeaderBar themeName={themeName} isDarkTheme={isDarkTheme} />
      )}

      <Content className={isAuthPage ? "auth-content" : "app-content"}>
        <div key={location.pathname} className="route-animate">
          <Routes>
            <Route path="/" element={<Navigate to="/auth" replace />} />
            <Route path="/auth" element={<Auth />} />

            <Route path="/home" element={<Home groupByDay={groupByDay} />} />
            <Route path="/post/:id" element={<PostDetail />} />
            <Route
              path="/archive"
              element={
                <RequireAuth>
                  <Archive />
                </RequireAuth>
              }
            />
            <Route path="/about" element={<About />} />
            <Route
              path="/write"
              element={
                <RequireAuth>
                  <WritePost />
                </RequireAuth>
              }
            />
            <Route
              path="/write/:id"
              element={
                <RequireAuth>
                  <WritePost />
                </RequireAuth>
              }
            />
            <Route
              path="/me"
              element={
                <RequireAuth>
                  <Me />
                </RequireAuth>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <Settings
                    themeName={themeName}
                    onThemeChange={setThemeName}
                    layoutColumns={layoutColumns}
                    onLayoutChange={setLayoutColumns}
                    groupByDay={groupByDay}
                    onGroupByDayChange={setGroupByDay}
                    bgImageEnabled={bgImageEnabled}
                    onBgImageEnabledChange={setBgImageEnabled}
                    bgImageUrl={bgImageUrl}
                    onBgImageUrlChange={setBgImageUrl}
                    bgVideoEnabled={bgVideoEnabled}
                    onBgVideoEnabledChange={setBgVideoEnabled}
                    bgVideoUrl={bgVideoUrl}
                    onBgVideoUrlChange={setBgVideoUrl}
                    bgFocusEnabled={bgFocusEnabled}
                    onBgFocusEnabledChange={setBgFocusEnabled}
                    customTheme={customTheme}
                    onCustomThemeChange={setCustomTheme}
                    customThemeDefaults={CUSTOM_THEME_DEFAULT}
                    lastNonCustomTheme={lastNonCustomTheme}
                  />
                </RequireAuth>
              }
            />

            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </div>
      </Content>

      {/* ✅ 登录页只留一个底部 */}
      {isAuthPage ? (
        <div
          className="app-auth-footer"
        >
          © {new Date().getFullYear()} my-blog-app
        </div>
      ) : (
        <FooterItem />
      )}

      <Modal
        title="功能指示"
        open={introOpen}
        onCancel={() => {
          setBgVideoEnabled(false);
          setIntroOpen(false);
        }}
        footer={[
          <Button
            key="no"
            onClick={() => {
              setBgVideoEnabled(false);
              setIntroOpen(false);
            }}
          >
            暂不开启
          </Button>,
          <Button
            key="yes"
            type="primary"
            onClick={() => {
              setBgVideoEnabled(true);
              setIntroOpen(false);
            }}
          >
            立即开启
          </Button>,
        ]}
      >
        <Space direction="vertical" size={10}>
  <div>欢迎使用，本次示例包含：</div>
  <div>
    - 首页文章列表 + 搜索 + 标签筛选
    <br />- 登录后可写文章
    <br />- 文章详情页
    <br />- 归档（按月份）
  </div>
  <div>是否启用背景视频？</div>
  <div>可以在“设置”中随时开关或更换视频。</div>
</Space>
      </Modal>
    </Layout>
  );
}

